import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Badge as BadgeIcon,
  Lock as LockIcon,
  LocalShipping as TruckIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from '../LanguageSwitcher';

const DemoCrewLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);

    window.setTimeout(() => {
      navigate('/crew', { replace: true });
    }, 900);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #000062 0%, #0b3d5c 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 3 },
        py: 3,
      }}
    >
      <Container maxWidth="sm" disableGutters>
        <Paper
          elevation={18}
          sx={{
            position: 'relative',
            p: { xs: 2.5, sm: 4 },
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.96)',
          }}
        >
          <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
            <LanguageSwitcher variant="menu" />
          </Box>

          <Stack spacing={2.5} alignItems="center" sx={{ mb: 3, pt: 1 }}>
            <Avatar
              sx={{
                width: 72,
                height: 72,
                bgcolor: '#de6127',
                color: '#fff',
              }}
            >
              <TruckIcon sx={{ fontSize: 38 }} />
            </Avatar>

            <Box sx={{ textAlign: 'center', px: { xs: 1, sm: 4 } }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{ color: '#000062', fontWeight: 700, mb: 0.75 }}
              >
                {t('demoCrewLogin.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('demoCrewLogin.subtitle')}
              </Typography>
            </Box>
          </Stack>

          <Card elevation={0} sx={{ bgcolor: 'transparent' }}>
            <CardContent sx={{ p: { xs: 0, sm: 1 } }}>
              <Box component="form" onSubmit={handleSubmit} data-demo-target="crew-demo-login-form">
                <Stack spacing={2.25}>
                  <TextField
                    fullWidth
                    label={t('auth.username')}
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    required
                    autoComplete="username"
                    inputProps={{
                      'aria-label': t('demoCrewLogin.usernameAria'),
                      'data-demo-target': 'crew-demo-login-username',
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label={t('auth.password')}
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    autoComplete="current-password"
                    inputProps={{
                      'aria-label': t('demoCrewLogin.passwordAria'),
                      'data-demo-target': 'crew-demo-login-password',
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: 'rgba(0, 0, 98, 0.04)',
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <BadgeIcon color="secondary" />
                      <Typography variant="body2" color="text.secondary">
                        {t('demoCrewLogin.helper')}
                      </Typography>
                    </Stack>
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    aria-label={t('demoCrewLogin.signInAria')}
                    data-demo-target="crew-demo-login-submit"
                    sx={{
                      minHeight: 54,
                      borderRadius: 2,
                      bgcolor: '#000062',
                      fontSize: '1.05rem',
                      fontWeight: 700,
                      '&:hover': { bgcolor: '#000043' },
                    }}
                  >
                    {loading ? t('demoCrewLogin.signingIn') : t('auth.signIn')}
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Paper>
      </Container>
    </Box>
  );
};

export default DemoCrewLogin;
